import { clearAdminOrgImpersonation } from "@/lib/auth/admin-org-impersonation"

/**
 * B2B → B2C session bridge.
 *
 * MIGRATION AUX — remove after Stytch B2B migration is complete.
 *
 * During the migration, B2B pages still render inside the B2C provider tree
 * (OrganizationContext, getSupabase, etc.). This module acts as a bridge:
 *
 * - b2b-context.tsx WRITES the B2B session data here after successful auth
 * - OrganizationContext READS it as a fallback when no B2C session exists
 *
 * Data is persisted in sessionStorage so it survives full page navigations
 * (e.g. from /login to /settings) but is cleared when the tab closes.
 */

const STORAGE_KEY = "cardinal_b2b_bridge"

interface B2BSessionData {
    email: string
    orgDomain: string
    supabaseToken: string
    /** MIGRATION AUX — admin flag resolved from Stytch B2B roles + email domain. */
    isAdmin?: boolean
    /** Whether the user can manage org members (cardinal_super_admin or stytch_admin). */
    canManageMembers?: boolean
}

/** Called by b2b-context after successful Stytch B2B auth + Supabase token exchange. */
export function setB2BSessionBridge(data: B2BSessionData) {
    if (typeof window === "undefined") return
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    // Tell listeners (OrganizationContext) the bridge is now populated, so they
    // can re-read sessionStorage and trigger their org fetch. Without this,
    // OrganizationContext only sees the bridge on a full page reload — leading
    // to a momentary "No organization found" flash on first login.
    window.dispatchEvent(new CustomEvent("b2b-bridge-changed"))
}

/** Called by OrganizationContext to check for B2B fallback credentials. */
export function getB2BSessionBridge(): B2BSessionData | null {
    if (typeof window === "undefined") return null
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

/**
 * MIGRATION AUX — Update just the isAdmin flag on an existing bridge entry
 * and notify listeners (OrganizationContext) via a custom event.
 */
export function updateB2BBridgeAdmin(isAdmin: boolean) {
    if (typeof window === "undefined") return
    const bridge = getB2BSessionBridge()
    if (bridge) {
        setB2BSessionBridge({ ...bridge, isAdmin })
    }
    window.dispatchEvent(new CustomEvent("b2b-admin-changed", { detail: { isAdmin } }))
}

/** Clear on logout. */
export function clearB2BSessionBridge() {
    if (typeof window === "undefined") return
    sessionStorage.removeItem(STORAGE_KEY)
    window.dispatchEvent(new CustomEvent("b2b-bridge-changed"))
}

/**
 * MIGRATION AUX — Full B2B logout cleanup.
 * Call from any logout flow to clear all B2B state.
 * Remove after B2C migration is complete.
 */
export function logoutB2BSession() {
    if (typeof window === "undefined") return
    clearAdminOrgImpersonation()
    clearB2BSessionBridge()
    document.cookie = "stytch_b2b_session=; path=/; max-age=0; SameSite=Lax"
    document.cookie = "stytch_b2b_session_jwt=; path=/; max-age=0; SameSite=Lax"
}

/**
 * MIGRATION AUX — Clear B2C session when entering B2B login.
 * Prevents stale B2C state from interfering with B2B auth.
 * Remove after B2C migration is complete.
 */
export function logoutB2CSession() {
    if (typeof window === "undefined") return
    clearAdminOrgImpersonation()
    document.cookie = "stytch_session=; path=/; max-age=0; SameSite=Lax"
    document.cookie = "stytch_session_jwt=; path=/; max-age=0; SameSite=Lax"
}
